'use client';

import dynamic from 'next/dynamic';
import { memo, useCallback, useMemo, useState } from 'react';
import { Copy, Maximize2 } from 'lucide-react';

import Link from 'next/link';

import { AnimatedContainer } from '@/components/ui/animated-container';
import { Button } from '@/components/ui/button';
import { PreviewDataTable } from '@/components/preview/PreviewDataTable';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { SurfacePanel } from '@/components/site/page-primitives';
import { useSession } from 'next-auth/react';
import { useProgressiveRows } from '@/hooks/use-lazy-dataset-rows';
import { useToast } from '@/hooks/use-toast';
import { TEMPLATE_TAG_LABELS } from '@/lib/templateCatalogFilters';
import { formatUtcDateTime } from '@/lib/time';
import type { TemplateDetail, TemplatePreviewResponse } from '@/types/templates';

type TemplateDetailClientProps = {
  template: TemplateDetail;
  preview: TemplatePreviewResponse | null;
};

type FaqItem = {
  question: string;
  answer: string;
};

type JsonBlockProps = {
  json: string | null;
};

const LOGIN_REQUIREMENT_LABELS: Record<TemplateDetail['loginRequirement'], string> = {
  none: 'Вход не нужен',
  possible: 'Вход может понадобиться',
  required: 'Требуется вход',
};
const CAPTCHA_RISK_LABELS: Record<TemplateDetail['captchaRisk'], string> = {
  low: 'Низкий риск CAPTCHA',
  medium: 'Средний риск CAPTCHA',
  high: 'Высокий риск CAPTCHA',
};
const DATA_SENSITIVITY_LABELS: Record<TemplateDetail['dataSensitivity'], string> = {
  business: 'Бизнес-данные',
  public_profile: 'Публичные профильные данные',
  personal_data_possible: 'Возможны персональные данные',
};
const INLINE_PREVIEW_ROW_LIMIT = 5;
const INLINE_PREVIEW_COLUMN_LIMIT = 6;
const INLINE_PREVIEW_COLUMN_PRIORITY = [
  'title',
  'price',
  'old_price',
  'availability',
  'rating',
  'review_count',
  'product_id',
  'brand',
  'seller_name',
];
const INLINE_PREVIEW_TECHNICAL_COLUMNS = new Set(['listing_url', 'image_url']);

const TemplateInteractivePanel = dynamic(
  () => import('./TemplateInteractivePanel').then((mod) => mod.TemplateInteractivePanel),
);

function formatList(items: string[]): string {
  if (items.length === 0) {
    return '';
  }
  if (items.length === 1) {
    return items[0] ?? '';
  }
  if (items.length === 2) {
    return `${items[0]} и ${items[1]}`;
  }
  return `${items.slice(0, -1).join(', ')}, и ${items[items.length - 1]}`;
}

const SampleJsonBlock = memo(function SampleJsonBlock({ json }: JsonBlockProps) {
  if (!json) {
    return <p className="text-sm text-[var(--site-ink-muted)]">Данные превью пока недоступны.</p>;
  }
  return (
    <pre className="overflow-auto rounded-lg border bg-muted p-4 text-xs" style={{ margin: 0, whiteSpace: 'pre' }}>
      <code>{json}</code>
    </pre>
  );
});

export function TemplateDetailClient({ template, preview }: TemplateDetailClientProps) {
  const { status } = useSession();
  const isAuthenticated = status === 'authenticated';
  const { toast } = useToast();
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);

  const sourceSummary = useMemo(() => {
    const sources = template.sourceDomains ?? [];
    if (!sources.length) {
      return 'Источники: —';
    }
    const visibleCount = 4;
    if (sources.length <= visibleCount) {
      return `Источники: ${sources.join(', ')}`;
    }
    const visible = sources.slice(0, visibleCount).join(', ');
    const remaining = sources.length - visibleCount;
    return `Источники: ${visible} +${remaining} еще`;
  }, [template.sourceDomains]);

  const coverageLine = useMemo(() => {
    const count = template.sourceDomains?.length ?? 0;
    if (!count) {
      return null;
    }
    return `Охват: ${count} источников`;
  }, [template.sourceDomains]);

  const progressivePreviewRows = useProgressiveRows(preview?.rows ?? [], 20);
  const publicPreviewColumns = useMemo(
    () =>
      progressivePreviewRows.columns.length > 0
        ? progressivePreviewRows.columns
        : template.outputSchema.map((field) => field.key),
    [progressivePreviewRows.columns, template.outputSchema],
  );
  const previewColumnTypes = useMemo(
    () =>
      template.outputSchema.reduce<Record<string, string>>((types, field) => {
        types[field.key] = field.type;
        return types;
      }, {}),
    [template.outputSchema],
  );
  const inlinePreviewRows = useMemo(
    () => progressivePreviewRows.rows.slice(0, INLINE_PREVIEW_ROW_LIMIT),
    [progressivePreviewRows.rows],
  );
  const inlinePreviewColumns = useMemo(() => {
    const columnsWithValues = publicPreviewColumns.filter((column) =>
      inlinePreviewRows.some((row) => {
        const value = row[column];
        return value != null && String(value).trim().length > 0;
      }),
    );
    const availableColumns = columnsWithValues.length > 0 ? columnsWithValues : publicPreviewColumns;
    const selected: string[] = [];
    const addColumn = (column: string) => {
      if (selected.length >= INLINE_PREVIEW_COLUMN_LIMIT || selected.includes(column)) {
        return;
      }
      selected.push(column);
    };

    INLINE_PREVIEW_COLUMN_PRIORITY.forEach((column) => {
      if (availableColumns.includes(column)) {
        addColumn(column);
      }
    });

    availableColumns
      .filter((column) => !INLINE_PREVIEW_TECHNICAL_COLUMNS.has(column))
      .forEach(addColumn);

    if (selected.length > 0) {
      return selected;
    }

    return availableColumns.slice(0, INLINE_PREVIEW_COLUMN_LIMIT);
  }, [inlinePreviewRows, publicPreviewColumns]);
  const previewHasRows = progressivePreviewRows.totalRows > 0;

  const normalizePreviewValue = useCallback((value: unknown) => {
    if (value == null) return '—';
    if (typeof value === 'string') {
      return value.replace(/^([€£$¥]){2,}/, '$1');
    }
    return value;
  }, []);

  const previewJsonRow = useMemo(() => {
    if (!preview?.rows?.length) {
      return null;
    }
    const row = preview.rows[0];
    return Object.fromEntries(Object.entries(row).map(([key, value]) => [key, normalizePreviewValue(value)]));
  }, [normalizePreviewValue, preview]);

  const previewJson = useMemo(
    () => (previewJsonRow ? JSON.stringify(previewJsonRow, null, 2) : null),
    [previewJsonRow],
  );

  const handleCopyPreviewJson = useCallback(async () => {
    if (!previewJson) return;
    try {
      await navigator.clipboard.writeText(previewJson);
      toast({
        title: 'Пример скопирован',
        description: 'JSON примера результата скопирован в буфер обмена.',
      });
    } catch {
      toast({
        variant: 'destructive',
        title: 'Не удалось скопировать',
        description: 'Не удалось скопировать пример результата.',
      });
    }
  }, [previewJson, toast]);

  const requiredParameterLabels = useMemo(
    () =>
      template.parameters
        .filter((param) => param.required)
        .map((param) => param.label.trim())
        .filter((label) => label.length > 0),
    [template.parameters],
  );

  const outputFieldKeys = useMemo(
    () =>
      template.outputSchema
        .map((field) => field.key.trim())
        .filter((key) => key.length > 0),
    [template.outputSchema],
  );

  const extractionAnswer = useMemo(() => {
    const parts: string[] = [];
    const descriptionText = template.description?.trim();
    if (descriptionText) {
      parts.push(descriptionText);
    }
    const sampleFields = outputFieldKeys.slice(0, 6);
    if (sampleFields.length > 0) {
      parts.push(`Основные поля включают ${sampleFields.join(', ')}.`);
    }
    parts.push('Полный список полей указан в схеме ниже.');
    return parts.join(' ');
  }, [outputFieldKeys, template.description]);

  const useCasesAnswer = useMemo(() => {
    if (template.useCases.length === 0) {
      return 'Сценарии использования зависят от источника данных и структуры сайта.';
    }
    return `Лучше всего подходит для ${formatList(template.useCases)}.`;
  }, [template.useCases]);

  const runAnswer = useMemo(() => {
    const requiredInputs = requiredParameterLabels.length
      ? `Укажите ${formatList(requiredParameterLabels)}, затем задайте лимиты запуска и запустите шаблон.`
      : 'Укажите входные данные, задайте лимиты запуска и запустите шаблон.';
    return `${requiredInputs} При наличии можно также выбрать совместимый исходный датасет.`;
  }, [requiredParameterLabels]);

  const outputFormatAnswer = useMemo(() => {
    const summary = template.outputSummary?.trim();
    const summarySentence = summary
      ? `На выходе вы получаете ${summary}.`
      : 'На выходе вы получаете структурированные строки по выходной схеме шаблона.';
    return `${summarySentence} Результаты можно открыть как датасет или экспортировать.`;
  }, [template.outputSummary]);

  const limitationsAnswer = useMemo(() => {
    const limitations = template.limitations?.trim();
    return limitations || 'Охват зависит от структуры исходной страницы и ограничений шаблона.';
  }, [template.limitations]);

  const faqItems: FaqItem[] = useMemo(
    () => [
      {
        question: 'Какие данные извлекает этот шаблон?',
        answer: extractionAnswer,
      },
      {
        question: 'Для каких задач парсинга подходит этот шаблон?',
        answer: useCasesAnswer,
      },
      {
        question: 'Как запустить парсинг по этому шаблону?',
        answer: runAnswer,
      },
      {
        question: 'Когда происходит тарификация?',
        answer:
          'Запуск шаблона расходует запросы API. Публичные превью показывают только опубликованные реальные строки и доступны бесплатно.',
      },
      {
        question: 'В каком формате возвращаются данные парсинга?',
        answer: outputFormatAnswer,
      },
      {
        question: 'Насколько актуально публичное превью?',
        answer:
          'Публичное превью обновляется только после публикации новой реальной выборки из активного датасета шаблона.',
      },
      {
        question: 'Какие ограничения у парсинга по этому шаблону?',
        answer: limitationsAnswer,
      },
    ],
    [extractionAnswer, limitationsAnswer, outputFormatAnswer, runAnswer, useCasesAnswer],
  );

  const faqJsonLd = useMemo(
    () => ({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqItems.map((item) => ({
        '@type': 'Question',
        name: item.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: item.answer,
        },
      })),
    }),
    [faqItems],
  );

  return (
    <AnimatedContainer animation="fadeIn" className="space-y-8">
      <section className="space-y-4">
        <div className="space-y-3">
          <h1 className="text-3xl font-bold text-[var(--site-ink-strong)]">{template.name}</h1>
          <p className="text-[var(--site-ink-muted)]">{template.description}</p>
          <div className="flex flex-wrap items-center gap-2">
            {template.tags.map((tag) => (
              <span
                key={`${template.id}-${tag}`}
                className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-[var(--site-ink)]"
              >
                {TEMPLATE_TAG_LABELS[tag] ?? tag}
              </span>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded-full bg-amber-100 px-3 py-1 text-xs font-medium text-amber-800">
              {LOGIN_REQUIREMENT_LABELS[template.loginRequirement]}
            </span>
            <span className="rounded-full bg-rose-100 px-3 py-1 text-xs font-medium text-rose-700">
              {CAPTCHA_RISK_LABELS[template.captchaRisk]}
            </span>
            <span className="rounded-full bg-blue-100 px-3 py-1 text-xs font-medium text-blue-700">
              {DATA_SENSITIVITY_LABELS[template.dataSensitivity]}
            </span>
          </div>
          <p className="text-xs text-[var(--site-ink-muted)]">{sourceSummary}</p>
          {coverageLine ? <p className="text-xs text-[var(--site-ink-muted)]">{coverageLine}</p> : null}
          <div className="flex flex-wrap gap-2 text-xs text-[var(--site-ink-muted)]">
            <span>Опубликовано строк: {preview?.actualRowCount ?? template.previewActualRows ?? template.previewRows}</span>
            {preview?.refreshedAt ? <span>Обновлено: {formatUtcDateTime(preview.refreshedAt)}</span> : null}
            {template.previewScenarioCount ? <span>Сценариев: {template.previewScenarioCount}</span> : null}
            {template.hasPublicUtilityDataset && template.publicUtilityThemeLabel ? (
              <span>Открытый датасет: {template.publicUtilityThemeLabel}</span>
            ) : null}
          </div>
          <div className="flex flex-wrap gap-3 pt-2">
            {isAuthenticated ? (
              <Button asChild className="bg-[var(--site-accent-text)] hover:bg-[var(--site-accent-text)]">
                <a href="#run-template">Запустить шаблон</a>
              </Button>
            ) : (
              <Button asChild className="bg-[var(--site-accent-text)] hover:bg-[var(--site-accent-text)]">
                <Link href={`/signup?redirect=${encodeURIComponent(`/templates/${template.id}`)}`}>
                  Попробуйте бесплатно
                </Link>
              </Button>
            )}
            <Button asChild variant="outline">
              <a href="#public-preview">Посмотреть пример данных</a>
            </Button>
          </div>
        </div>
      </section>

      <section aria-labelledby="template-highlights" className="space-y-3">
        <h2 id="template-highlights" className="text-xl font-semibold text-[var(--site-ink-strong)]">
          Обзор шаблона
        </h2>
        <div className="grid gap-4 md:grid-cols-2">
          <SurfacePanel className="space-y-2">
            <h3 className="text-sm font-semibold text-[var(--site-ink)]">Сценарии использования</h3>
            <ul className="space-y-1 text-sm text-[var(--site-ink)]">
              {template.useCases.map((useCase, index) => (
                <li key={`${template.id}-use-case-${index}`} className="flex items-start gap-2">
                  <span className="mt-1 h-1.5 w-1.5 rounded-full bg-slate-400" />
                  <span>{useCase}</span>
                </li>
              ))}
            </ul>
          </SurfacePanel>

          <SurfacePanel className="space-y-2">
            <h3 className="text-sm font-semibold text-[var(--site-ink)]">Сводка результата</h3>
            <p className="text-sm text-[var(--site-ink)]">{template.outputSummary}</p>
          </SurfacePanel>

          <SurfacePanel className="space-y-2">
            <h3 className="text-sm font-semibold text-[var(--site-ink)]">Примечания по входным данным</h3>
            <ul className="space-y-1 text-sm text-[var(--site-ink)]">
              {template.inputNotes.map((note, index) => (
                <li key={`${template.id}-input-note-${index}`} className="flex items-start gap-2">
                  <span className="mt-1 h-1.5 w-1.5 rounded-full bg-slate-400" />
                  <span>{note}</span>
                </li>
              ))}
            </ul>
          </SurfacePanel>

          <SurfacePanel className="space-y-2">
            <h3 className="text-sm font-semibold text-[var(--site-ink)]">Ограничения</h3>
            <p className="text-sm text-[var(--site-ink)]">{template.limitations}</p>
          </SurfacePanel>

          <SurfacePanel className="space-y-2">
            <h3 className="text-sm font-semibold text-[var(--site-ink)]">Примечания по соответствию</h3>
            {template.legalNotes.length === 0 ? (
              <p className="text-sm text-[var(--site-ink)]">Дополнительные юридические примечания не указаны.</p>
            ) : (
              <ul className="space-y-1 text-sm text-[var(--site-ink)]">
                {template.legalNotes.map((note, index) => (
                  <li key={`${template.id}-legal-note-${index}`} className="flex items-start gap-2">
                    <span className="mt-1 h-1.5 w-1.5 rounded-full bg-slate-400" />
                    <span>{note}</span>
                  </li>
                ))}
              </ul>
            )}
          </SurfacePanel>
        </div>
      </section>

      <section id="public-preview" className="scroll-mt-24">
        <SurfacePanel className="min-w-0 space-y-4">
          <div className="space-y-1">
            <div className="flex items-start justify-between gap-4">
              <h2 className="text-lg font-semibold text-[var(--site-ink-strong)]">Публичное превью</h2>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setPreviewDialogOpen(true)}
                disabled={!previewHasRows}
              >
                <Maximize2 className="h-4 w-4" />
                На весь экран
              </Button>
            </div>
            <p className="text-sm text-[var(--site-ink-muted)]">
              Короткий пример опубликованных строк. Полная таблица со всеми колонками открывается на весь экран.
            </p>
          </div>
          <PreviewDataTable
            tableId={`${template.id}-public-preview-inline`}
            rows={inlinePreviewRows}
            columns={inlinePreviewColumns}
            columnTypes={previewColumnTypes}
            emptyMessage="Превью пока не опубликовано."
            variant="compact"
            enableColumnControls={false}
            pinFirstColumn={false}
            normalizeValue={normalizePreviewValue}
            showRowCount={false}
          />
        </SurfacePanel>
      </section>

      <section>
        <div className="grid gap-4 md:grid-cols-3">
          {[
            {
              title: '1. Укажите входные данные',
              body: 'Используйте ручные поля или выберите совместимый исходный датасет и сопоставление.',
            },
            {
              title: '2. Запустите шаблон',
              body: 'Шаблон отправит запрос к API и соберет данные с указанных страниц.',
            },
            {
              title: '3. Получите датасет',
              body: 'Сразу просмотрите строки и откройте датасет для полного результата.',
            },
          ].map((step) => (
            <SurfacePanel key={step.title} className="space-y-2">
              <h3 className="text-sm font-semibold text-[var(--site-ink)]">{step.title}</h3>
              <p className="text-sm text-[var(--site-ink)]">{step.body}</p>
            </SurfacePanel>
          ))}
        </div>
      </section>

      <TemplateInteractivePanel template={template} isAuthenticated={isAuthenticated} />

      <section id="sample-output" className="scroll-mt-24">
        <SurfacePanel className="space-y-4">
          <div className="space-y-1">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h2 className="text-lg font-semibold text-[var(--site-ink-strong)]">Пример результата (JSON)</h2>
              <Button variant="outline" size="sm" onClick={handleCopyPreviewJson} disabled={!previewJson}>
                <Copy className="h-4 w-4" />
                Скопировать JSON
              </Button>
            </div>
            <p className="text-sm text-[var(--site-ink-muted)]">Пример строки на основе данных публичного превью.</p>
          </div>
          <SampleJsonBlock json={previewJson} />
        </SurfacePanel>
      </section>

      <section id="faq" className="scroll-mt-24">
        <SurfacePanel className="space-y-4">
          <div className="space-y-1">
            <h2 className="text-lg font-semibold text-[var(--site-ink-strong)]">Частые вопросы</h2>
            <p className="text-sm text-[var(--site-ink-muted)]">Ответы на частые вопросы об этом шаблоне.</p>
          </div>
          <div className="space-y-4">
            {faqItems.map((item) => (
              <div key={item.question} className="space-y-1">
                <p className="text-sm font-semibold text-[var(--site-ink-strong)]">{item.question}</p>
                <p className="text-sm text-[var(--site-ink)]">{item.answer}</p>
              </div>
            ))}
          </div>
        </SurfacePanel>
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }} />
      </section>

      <Dialog open={previewDialogOpen} onOpenChange={setPreviewDialogOpen}>
        <DialogContent className="flex h-[90vh] max-w-[95vw] flex-col overflow-hidden">
          <DialogHeader>
            <DialogTitle>Публичное превью</DialogTitle>
            <DialogDescription>
              Кэшированные примерные данные. Обновляются, когда администратор публикует новое превью.
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-auto rounded-lg border border-[color:var(--site-border-soft)] p-4">
            <div className="space-y-3">
              <PreviewDataTable
                tableId={`${template.id}-public-preview-dialog`}
                rows={progressivePreviewRows.rows}
                columns={publicPreviewColumns}
                columnTypes={previewColumnTypes}
                emptyMessage="Превью пока не опубликовано."
                normalizeValue={normalizePreviewValue}
                hasMore={progressivePreviewRows.hasMore}
                isLoadingMore={progressivePreviewRows.isLoading}
                onLoadMore={progressivePreviewRows.loadNext}
                loadedCount={progressivePreviewRows.loadedCount}
                totalCount={progressivePreviewRows.totalRows}
              />
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </AnimatedContainer>
  );
}
